import { useEffect, useState } from "react";
import { styles } from "../../styles";
import { ErrorNote } from "../common";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../hooks/useAuth";
import {
  listStaff, createStaff, setStaffAdmin, setStaffPassword, deleteStaff,
  friendlyUserError, type ManagedUser,
} from "../../data/users";

function whenLabel(iso: string | null): string {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "unknown";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function TeamCard() {
  const notify = useToast();
  const { user } = useAuth();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyUid, setBusyUid] = useState<string | null>(null);

  // Add-user form
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newAdmin, setNewAdmin] = useState(true);
  const [adding, setAdding] = useState(false);

  // Inline set-password
  const [pwForUid, setPwForUid] = useState<string | null>(null);
  const [pwValue, setPwValue] = useState("");

  const refresh = async () => {
    setError(null);
    try {
      setUsers(await listStaff());
    } catch (err) {
      setError(friendlyUserError(err, "We could not load the team. Please refresh and try again."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const add = async () => {
    setError(null);
    setAdding(true);
    try {
      await createStaff(newEmail.trim(), newPassword, newAdmin);
      setNewEmail(""); setNewPassword(""); setNewAdmin(true);
      notify("Account created.");
      await refresh();
    } catch (err) {
      setError(friendlyUserError(err, "We could not create that account. Please try again."));
    } finally {
      setAdding(false);
    }
  };

  const toggleAdmin = async (u: ManagedUser) => {
    setError(null);
    setBusyUid(u.uid);
    try {
      await setStaffAdmin(u.uid, !u.admin);
      notify(u.admin ? "Admin access removed." : "Admin access granted.");
      await refresh();
    } catch (err) {
      setError(friendlyUserError(err, "We could not change that account. Please try again."));
    } finally {
      setBusyUid(null);
    }
  };

  const savePassword = async (u: ManagedUser) => {
    setError(null);
    setBusyUid(u.uid);
    try {
      await setStaffPassword(u.uid, pwValue);
      setPwForUid(null); setPwValue("");
      notify("Password updated.");
    } catch (err) {
      setError(friendlyUserError(err, "We could not set that password. Please try again."));
    } finally {
      setBusyUid(null);
    }
  };

  const remove = async (u: ManagedUser) => {
    setError(null);
    if (!window.confirm(`Delete the account ${u.email}? This cannot be undone.`)) return;
    setBusyUid(u.uid);
    try {
      await deleteStaff(u.uid);
      notify("Account deleted.");
      await refresh();
    } catch (err) {
      setError(friendlyUserError(err, "We could not delete that account. Please try again."));
    } finally {
      setBusyUid(null);
    }
  };

  return (
    <div style={styles.card}>
      <h2 style={styles.cardTitle}>Team &amp; admins</h2>
      <p style={styles.sub}>
        Staff accounts that can sign in here. Admins can manage everything; grant or remove admin,
        reset a password, or remove an account.
      </p>
      {error && <ErrorNote>{error}</ErrorNote>}

      {loading ? (
        <p style={styles.sub}>Loading the team&hellip;</p>
      ) : (
        <div style={styles.breedTable}>
          {users.map((u) => {
            const isSelf = user?.uid === u.uid;
            const rowBusy = busyUid === u.uid;
            return (
              <div key={u.uid} style={{ padding: "12px 0", borderBottom: "1px solid var(--line)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 560 }}>
                      {u.email}{isSelf && <span style={{ color: "var(--muted)", fontWeight: 400 }}> (you)</span>}
                    </div>
                    <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
                      Last sign in: {whenLabel(u.lastSignIn)}
                    </div>
                  </div>
                  {u.admin && (
                    <span style={{
                      fontSize: 11.5, fontWeight: 620, color: "var(--accent-d)", background: "var(--accent-soft)",
                      padding: "3px 9px", borderRadius: 999,
                    }}>Admin</span>
                  )}
                  <button
                    className="ghost" style={styles.ghostSm} disabled={rowBusy || (u.admin && isSelf)}
                    onClick={() => toggleAdmin(u)}
                    title={u.admin && isSelf ? "You cannot remove your own admin access" : undefined}
                  >
                    {u.admin ? "Remove admin" : "Make admin"}
                  </button>
                  <button
                    className="ghost" style={styles.ghostSm} disabled={rowBusy}
                    onClick={() => { setPwForUid(pwForUid === u.uid ? null : u.uid); setPwValue(""); }}
                  >
                    Set password
                  </button>
                  <button
                    className="link" style={styles.removeBtn} disabled={rowBusy || isSelf}
                    onClick={() => remove(u)}
                    title={isSelf ? "You cannot delete your own account" : undefined}
                  >
                    Delete
                  </button>
                </div>
                {pwForUid === u.uid && (
                  <div style={{ ...styles.addRow, marginTop: 10, marginBottom: 0 }}>
                    <input
                      type="password" value={pwValue} onChange={(e) => setPwValue(e.target.value)}
                      placeholder="New password (8+ characters)" autoComplete="new-password"
                      className="input" style={{ ...styles.input, flex: 1, minWidth: 160 }}
                      aria-label={`New password for ${u.email}`}
                    />
                    <button className="primary" style={styles.primarySm} disabled={rowBusy} onClick={() => savePassword(u)}>
                      Save
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
        <span style={styles.label}>Add a staff account</span>
        <div style={styles.addRow}>
          <input
            type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
            placeholder="email@example.com" autoComplete="off"
            className="input" style={{ ...styles.input, flex: 1, minWidth: 160 }} aria-label="New account email"
          />
          <input
            type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Password (8+)" autoComplete="new-password"
            className="input" style={{ ...styles.input, flex: 1, minWidth: 140 }} aria-label="New account password"
          />
          <button className="primary" style={styles.primarySm} onClick={add} disabled={adding}>
            {adding ? "Adding…" : "Add"}
          </button>
        </div>
        <label style={{ ...styles.checkRow, marginTop: 0 }}>
          <input type="checkbox" checked={newAdmin} onChange={(e) => setNewAdmin(e.target.checked)} style={styles.checkBox} />
          <span style={styles.checkLabel}>Give this account admin access.</span>
        </label>
      </div>
    </div>
  );
}
